import {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {MapeoEscaneosTabla} from '@/components/bateria/mapeo-escaneos-tabla'
import {formatSize} from '@/hooks/sgsst/evidencias/use-evidence'
import {useUploadScan, type BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {MAX_SCAN_BYTES, PDF_TYPE, ZIP_TYPES, type LotesResumen} from '@/lib/bateria'
import {proponerMapeo, type PersonaDelMapeo} from '@/lib/mapeo-archivos'
import {leerNombresDeEscaneos} from '@/lib/zip-nombres'
import {publicarAviso} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'

/** The map is {filename: paquete_id}, so a repeated filename silently drops one person's scan. Nothing
 * else is validated: a valid name is whatever the ZIP actually holds. */
function validateManifestFilenames(rows: [string, string][]): string | null {
  const names = rows.map(([, filename]) => filename.trim().toLowerCase()).filter(Boolean)
  return new Set(names).size === names.length ? null : 'admin.bateria.escaneos.manifestDuplicado'
}

function personasDelMapeo(paquetes: BateriaPaquete[]): PersonaDelMapeo[] {
  return paquetes.map((p) => ({id: p.id, nombre: p.employee_name, cedula: p.employee_cedula}))
}

type Props = {businessId: string; evaluacionId: string; lotes: LotesResumen} & (
  | {
      variant: 'paquete'
      paquete: BateriaPaquete
      paquetes?: never
      // Adenda §7: re-procesar un paquete ya leído, que el guard normal (generado/error) ocultaría.
      forzar?: boolean
      label?: string
    }
  | {variant: 'bulk'; paquetes: BateriaPaquete[]; paquete?: never}
)

export function SubirEscaneosControl(props: Props) {
  const {businessId, evaluacionId, variant, lotes} = props
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadScan(businessId, evaluacionId)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // `duplicate`: la subida terminó bien pero no se procesó nada, y hay que decirlo.
  const [duplicado, setDuplicado] = useState(false)
  // Held paquete_id → filename (the per-row shape), flipped to {filename: paquete_id} at submit.
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [manifest, setManifest] = useState<Record<string, string>>({})
  // `propuestos`: las filas que llenó el sistema, para distinguirlas de las escritas a mano.
  const [archivosDelZip, setArchivosDelZip] = useState<string[]>([])
  const [propuestos, setPropuestos] = useState<Set<string>>(new Set())
  const [leyendoZip, setLeyendoZip] = useState(false)
  // Ref de callback al `DialogContent`: sin contenedor el popover va a `document.body` y el primer
  // Escape cierra el diálogo entero (ver `popover.tsx`).
  const [nodoDialogo, setNodoDialogo] = useState<HTMLElement | null>(null)
  // Una lectura vieja que llegue tarde no puede pisar la propuesta de un ZIP elegido después.
  const zipEnLecturaRef = useRef<File | null>(null)
  const personas = useMemo(
    () => (props.variant === 'bulk' ? personasDelMapeo(props.paquetes) : []),
    [props.variant, props.paquetes]
  )

  async function uploadPaquetePdf(files: FileList | null) {
    if (variant !== 'paquete') return
    const file = files?.[0]
    if (!file) return
    setError(null)
    if (file.type !== PDF_TYPE) {
      setError(t('admin.bateria.escaneos.pdfOnly'))
      return
    }
    if (file.size > MAX_SCAN_BYTES) {
      setError(t('admin.bateria.escaneos.tooBig'))
      return
    }
    setDuplicado(false)
    try {
      setProgress(0)
      const registro = await upload.mutateAsync({file, paqueteId: props.paquete.id, onProgress: setProgress})
      setDuplicado(registro.status === 'duplicate')
      // Aviso, nunca bloqueo: subir encima de un cuadernillo a medias es legítimo. Va por avisos
      // porque dentro de una fila de tabla no cabe.
      if (registro.hojas_pendientes > 0) {
        publicarAviso(
          t('admin.bateria.escaneosV2.yaTeniaPendientesTitulo'),
          t('admin.bateria.escaneosV2.yaTeniaPendientes', {count: registro.hojas_pendientes})
        )
      }
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function pickZip(files: FileList | null) {
    const file = files?.[0]
    if (!file || props.variant !== 'bulk') return
    setError(null)
    if (!ZIP_TYPES.has(file.type)) {
      setError(t('admin.bateria.escaneos.zipOnly'))
      return
    }
    if (file.size > MAX_SCAN_BYTES) {
      setError(t('admin.bateria.escaneos.tooBig'))
      return
    }
    setZipFile(file)
    if (inputRef.current) inputRef.current.value = ''
    // La propuesta sale de la cola del ZIP, sin subirlo; si no se deja leer (zip64, anidado,
    // corrupto), el mapeo se llena a mano.
    zipEnLecturaRef.current = file
    setLeyendoZip(true)
    const nombres = await leerNombresDeEscaneos(file).catch(() => [])
    if (zipEnLecturaRef.current !== file) return
    setLeyendoZip(false)
    setArchivosDelZip(nombres)
    const propuesta = proponerMapeo(personasDelMapeo(props.paquetes), nombres)
    setManifest(propuesta)
    setPropuestos(new Set(Object.keys(propuesta)))
  }

  function cambiarFila(paqueteId: string, archivo: string) {
    setManifest((prev) => ({...prev, [paqueteId]: archivo}))
    setPropuestos((prev) => {
      if (!prev.has(paqueteId)) return prev
      const siguiente = new Set(prev)
      siguiente.delete(paqueteId)
      return siguiente
    })
  }

  async function uploadZip(e: React.FormEvent) {
    e.preventDefault()
    if (!zipFile) return
    setError(null)
    const filas = Object.entries(manifest)
    const duplicado = validateManifestFilenames(filas)
    if (duplicado) {
      setError(t(duplicado))
      return
    }
    const mapping: Record<string, string> = {}
    for (const [paqueteId, filename] of filas) {
      const name = filename.trim()
      if (name) mapping[name] = paqueteId
    }
    setDuplicado(false)
    try {
      setProgress(0)
      const registro = await upload.mutateAsync({file: zipFile, manifest: mapping, onProgress: setProgress})
      closeBulk()
      setDuplicado(registro.status === 'duplicate')
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setProgress(null)
    }
  }

  function closeBulk() {
    setZipFile(null)
    setManifest({})
    setArchivosDelZip([])
    setPropuestos(new Set())
    setLeyendoZip(false)
    zipEnLecturaRef.current = null
    setError(null)
    setProgress(null)
    upload.reset()
  }

  // §3.6: un lote vivo no bloquea: cada página hace UPSERT idempotente por (paquete_id, numero) y el
  // ensamblado va bajo `pg_advisory_xact_lock`. `enCurso` solo avisa.
  const enCurso = lotes.corriendo && !lotes.detenido

  if (variant === 'paquete') {
    // Only before it's read, unless `forzar` (adenda §7).
    if (!props.forzar && props.paquete.estado !== 'generado' && props.paquete.estado !== 'error') return null
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          data-testid={`escaneo-input-${props.paquete.id}`}
          onChange={(e) => uploadPaquetePdf(e.target.files)}
        />
        {progress !== null ? (
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
          </div>
        ) : (
          <Button
            size="xs"
            variant="ghost"
            data-testid={`escaneo-upload-${props.paquete.id}`}
            onClick={() => inputRef.current?.click()}>
            {props.label ?? t('admin.bateria.escaneos.subirPdf')}
          </Button>
        )}
        {error && <span className="text-xs text-destructive">{error}</span>}
        {duplicado && (
          <span className="inline-flex items-center gap-1">
            <span className="tone-amber rounded-sm px-1 text-xs" data-testid={`escaneo-duplicado-${props.paquete.id}`}>
              {t('admin.bateria.escaneos.duplicadoCorto')}
            </span>
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneos.duplicadoCorto')})}
              testId={`escaneo-duplicado-tip-${props.paquete.id}`}>
              {t('admin.bateria.escaneos.duplicadoTip')}
            </HelpIcon>
          </span>
        )}
      </>
    )
  }

  // Several ZIPs feed the same evaluación, so the button stays after a successful upload.
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        data-testid="subir-zip"
        disabled={props.paquetes.length === 0}
        onClick={() => inputRef.current?.click()}>
        {t('admin.bateria.escaneos.subirZip')}
      </Button>
      {/* Solo en ZIP: «puede subir más ZIP» nunca fue cierto en la subida por paquete. */}
      {enCurso && <Hint testId="subir-zip-en-curso">{t('admin.bateria.escaneos.enCursoAyuda')}</Hint>}
      <input
        ref={inputRef}
        type="file"
        accept="application/zip,.zip"
        hidden
        data-testid="escaneo-zip-input"
        onChange={(e) => pickZip(e.target.files)}
      />
      {/* A rejected pick never opens the dialog, so its footer error would never show; this is the
          only place the picker's own rejection appears. */}
      {!zipFile && error && (
        <p className="text-xs text-destructive" data-testid="escaneo-zip-pick-error">
          {error}
        </p>
      )}
      {duplicado && (
        <div
          className="tone-amber flex w-full items-center gap-1.5 rounded-md px-3 py-2"
          data-testid="escaneo-duplicado">
          <p className="text-sm font-medium">{t('admin.bateria.escaneos.duplicadoCorto')}</p>
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneos.duplicadoCorto')})}
            testId="escaneo-duplicado-tip">
            {t('admin.bateria.escaneos.duplicadoTip')}
          </HelpIcon>
        </div>
      )}
      <Dialog open={!!zipFile} onOpenChange={(open) => !open && closeBulk()}>
        <DialogContent
          ref={setNodoDialogo}
          className="max-h-[85vh] max-w-2xl overflow-y-auto"
          data-testid="escaneo-zip-dialog">
          <DialogHeader>
            <DialogTitle>{t('admin.bateria.escaneos.zipDialogTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={uploadZip} className="space-y-4" noValidate>
            {zipFile && (
              <p className="font-mono text-xs text-muted-foreground">
                {zipFile.name} · {formatSize(zipFile.size)}
              </p>
            )}
            {/* R5: el formato de una subida validada se ve en reposo, en una línea. */}
            <Hint testId="escaneo-formatos">{t('admin.bateria.escaneos.formatosZip')}</Hint>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-sm text-foreground">
                {t('admin.bateria.escaneos.manifestLabel')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneos.manifestLabel')})}
                  testId="manifest-help"
                  contenedor={nodoDialogo}>
                  {t('admin.bateria.escaneos.manifestHelp')}
                </HelpIcon>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="mapeo-lectura">
                {leyendoZip
                  ? t('admin.bateria.escaneos.mapeoLeyendo')
                  : archivosDelZip.length === 0
                    ? t('admin.bateria.escaneos.mapeoSinNombres')
                    : t('admin.bateria.escaneos.mapeoResumen', {
                        archivos: archivosDelZip.length,
                        propuestos: propuestos.size,
                      })}
              </p>
            </div>
            <MapeoEscaneosTabla
              personas={personas}
              archivos={archivosDelZip}
              mapeo={manifest}
              propuestos={propuestos}
              onCambiar={cambiarFila}
            />
            {progress !== null && (
              <div className="space-y-1" data-testid="escaneo-progreso">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
                </div>
                <p className="font-mono text-xs text-muted-foreground">
                  {t('admin.bateria.escaneos.subiendo', {pct: progress})}
                </p>
              </div>
            )}
            <FormDialogFooter
              error={error}
              pending={upload.isPending}
              submitLabel={t('admin.bateria.escaneos.zipSubmit')}
              onCancel={closeBulk}
            />
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
